import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Bot, ShieldCheck, Unplug, UserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loading, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useTelegramBotAccounts,
  useTelegramBusinessAccounts,
  useDeleteZaloAccount,
  telegramBusinessBotId,
} from '@/hooks/use-integrations'
import { SelectableTable, accountColumns, accountSearchText, ACCOUNT_FILTERS } from './accounts-table'
import { useMyPermissions } from '@/hooks/use-settings'

/**
 * Tab Telegram cá nhân — qua Telegram Business (connected bots), KHÔNG đăng
 * nhập hộ tài khoản (MTProto chưa làm và không khuyến nghị).
 *
 * Chủ tài khoản tự gắn bot eCDP trong app Telegram; bot nhận tin của các chat
 * riêng và trả lời bằng `business_connection_id` ⇒ khách thấy tin đứng tên
 * chính chủ. Mỗi kết nối hiện ra đây như một tài khoản kênh riêng.
 */
export function TelegramAppTab() {
  const { t } = useTranslation('chanZalo')
  const bots = useTelegramBotAccounts()
  const biz = useTelegramBusinessAccounts()
  const remove = useDeleteZaloAccount()
  // Kết nối bot mới = create, gỡ tài khoản khỏi hệ thống = DELETE /zalo-accounts/:id (delete).
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canDelete = !!perms?.has('integrations.delete')

  /** botId → tên hiển thị (@username) để ghi "qua @bot" dưới mỗi tài khoản. */
  const botName = useMemo(() => {
    const m = new Map<string, string>()
    for (const b of bots.data ?? []) if (b.externalPageId) m.set(b.externalPageId, b.displayName || 'bot')
    return m
  }, [bots.data])

  if (bots.isLoading) return <Loading label={t('tgApp.loading')} />

  const danhSachBot = (bots.data ?? []).filter((b) => !b.isDisabled)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserRound className="h-4 w-4" />
            {t('tgApp.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {danhSachBot.length === 0 ? (
            <EmptyState
              icon={Bot}
              title={t('tgApp.needBotTitle')}
              description={t('tgApp.needBotDesc')}
              action={
                canCreate && (
                  <Button asChild>
                    <Link to="/integrations/telegram-bot">{t('tgApp.connectBot')}</Link>
                  </Button>
                )
              }
            />
          ) : (
            <>
              <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
                <li>
                  <Trans t={t} i18nKey="tgApp.step1" components={{ b: <strong /> }} />
                </li>
                <li>
                  {t('tgApp.step2')}{' '}
                  {danhSachBot.map((b, i) => (
                    <span key={b.id}>
                      {i > 0 && t('tgApp.or')}
                      <strong className="font-mono text-foreground">{b.displayName}</strong>
                    </span>
                  ))}
                  .
                </li>
                <li>
                  <Trans t={t} i18nKey="tgApp.step3" components={{ b: <strong /> }} />
                </li>
                <li>{t('tgApp.step4')}</li>
              </ol>
              <div className="flex gap-2 rounded-md border border-success/40 bg-success/10 p-3">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <p className="text-muted-foreground">
                  {t('tgApp.official')}
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">{t('tgApp.listTitle')}</h3>
        <SelectableTable
          rows={biz.data}
          rowKey={(a) => a.id}
          loading={biz.isLoading}
          error={biz.isError}
          filters={ACCOUNT_FILTERS}
          searchText={accountSearchText}
          empty={
            <EmptyState
              icon={UserRound}
              title={t('tgApp.emptyTitle')}
              description={t('tgApp.emptyDesc')}
            />
          }
          columns={accountColumns({
            fallbackName: t('tgApp.fallbackName'),
            sub: (acc) => {
              const id = telegramBusinessBotId(acc)
              const ten = id ? botName.get(id) : undefined
              return ten ? t('tgApp.via', { bot: ten }) : undefined
            },
            actions: canDelete
              ? (acc) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    title={t('tgApp.stopTitle')}
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate(acc.id, {
                        onSuccess: () => toast.success(t('tgApp.stopped')),
                        onError: (e) => toast.error(apiError(e)),
                      })
                    }
                  >
                    <Unplug />
                  </Button>
                )
              : undefined,
          })}
        />
      </section>
    </div>
  )
}
